import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useSettings } from "../lib/useSettings";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/chi-siamo", label: "Chi Siamo" },
  { to: "/hotel", label: "Hotel" },
  { to: "/nuove-aperture", label: "Nuove Aperture" },
  { to: "/mercato-milano", label: "Mercato" },
  { to: "/news", label: "News" },
  { to: "/contatti", label: "Contatti" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const settings = useSettings();
  const bookingEnabled =
    settings?.booking_engine_enabled && !!settings?.booking_engine_url;
  const bookingLabel = settings?.booking_engine_label || "Prenota";
  const bookingUrl = settings?.booking_engine_url || "";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "py-3 bg-black/40 backdrop-blur-xl border-b border-white/5"
          : "py-5 bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src="/brand/invest-hospitality-logo.svg"
            alt="Invest Hospitality"
            className="h-9 md:h-10 w-auto"
            loading="eager"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace(/\//g, "") || "home"}`}
              end={n.to === "/"}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm transition-colors ${
                  isActive
                    ? "text-white bg-white/10"
                    : "text-white/60 hover:text-white"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {bookingEnabled && (
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-booking-engine"
              className="hidden md:inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] rounded-full px-4 py-2 bg-[#C48F56] text-black hover:bg-white transition-colors duration-300"
            >
              {bookingLabel}
            </a>
          )}
          <Link
            to="/investor"
            data-testid="nav-investor"
            className="hidden md:inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] border border-white/15 rounded-full px-4 py-2 hover:bg-[#C48F56] hover:text-black hover:border-[#C48F56] transition-colors duration-300"
          >
            Area Investitori
          </Link>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden h-10 w-10 grid place-items-center rounded-full border border-white/10 hover:bg-white/5"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden border-t border-white/5 bg-black/80 backdrop-blur-xl"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`mobile-nav-${n.to.replace(/\//g, "") || "home"}`}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-lg text-base ${
                      isActive ? "bg-white/10" : "text-white/70"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/investor"
                data-testid="mobile-nav-investor"
                className="mt-2 px-4 py-3 rounded-lg border border-white/10 text-sm tracking-wider uppercase"
              >
                Area Investitori
              </Link>
              {bookingEnabled && (
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-nav-booking-engine"
                  className="px-4 py-3 rounded-lg bg-[#C48F56] text-black text-sm tracking-wider uppercase text-center"
                >
                  {bookingLabel}
                </a>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
